import { Me } from '@yandex-int/k-common/typings';
import b from 'b_';
import cx from 'classnames';
import { isEqual } from 'lodash';
import { Select, Tabs, Text, SelectEvent } from 'platform-components';
import { POPUP_DIRECTIONS, SELECT_THEMES } from 'platform-components/constants';
import type { SUBJECTS_SLUGS_TYPE } from 'platform-components/src/typings';
import React, { useCallback } from 'react';
import ym from 'react-yandex-metrika';

import { CourseSubject } from 'store/courses/typings';

import { MANY_SUBJECTS } from '../constants';

import './subjects-nav__list.scss';

type Props<S extends CourseSubject = CourseSubject, V = string> = {
    subjects: S[];
    currentSubject: string;
    onSelect: (subject: string, parsed: V) => void;
    teamLevel: number | null;
    me?: Me | null;
    disabled?: boolean;
    metrikaParams?: Record<string, any>;
    mix?: string;
    stringify?: (s: S) => string;
} & (V extends SUBJECTS_SLUGS_TYPE
    ? {
          parse?: (val: string) => V;
      }
    : {
          parse: (val: string) => V;
      });

function SubjectsNavList<S extends CourseSubject = CourseSubject, V = string>(props: Props<S, V>) {
    const {
        onSelect,
        subjects,
        currentSubject,
        disabled,
        mix,
        me,
        metrikaParams,
        teamLevel,
        stringify = (s: S) => s.subject,
        parse = (v: string) => v as V,
    } = props;

    const selectSubject = useCallback(
        (subject: string) => {
            if (currentSubject !== subject) {
                const { location: locationForMetrika, ...otherMetrikaParams } = metrikaParams || {};

                ym('reachGoal', 'teacher_chose_subject', {
                    subject,
                    location: locationForMetrika,
                    teacher_id: me?.id,
                    team_level: teamLevel,
                    ...otherMetrikaParams,
                });

                onSelect(subject, parse(subject) as V);
            }
        },
        [currentSubject, onSelect, metrikaParams, me, teamLevel, parse]
    );

    const selectSubjectEvent = useCallback(
        ({ target: { value: subject } }: SelectEvent<string>) => {
            selectSubject(subject);
        },
        [selectSubject]
    );

    const hasManySubjects = subjects.length > MANY_SUBJECTS;

    return (
        <>
            <Select
                focused={false}
                mix={cx(b('subjects-nav', 'select', { 'many-subjects': hasManySubjects }), mix)}
                onSelect={selectSubjectEvent}
                popupDirection={POPUP_DIRECTIONS.BOTTOM}
                popupMix="subjects-nav__select-popup"
                theme={SELECT_THEMES.ROUNDED}
                value={currentSubject}
                width={208}
            >
                {subjects.map((subject: S) => {
                    const val = stringify(subject);

                    return (
                        <Select.Option key={val} mix="subjects-nav__select-item" value={val}>
                            <Text ellipsis>{subject.name}</Text>
                        </Select.Option>
                    );
                })}
            </Select>
            <Tabs
                mix={cx(b('subjects-nav', 'tabs', { 'many-subjects': hasManySubjects }), mix)}
                theme={Tabs.THEME.ROUNDED}
            >
                {subjects.map((subject: S) => {
                    const val = stringify(subject);
                    const isSelected = val === currentSubject;

                    return (
                        <Tabs.Tab
                            disabled={disabled}
                            id={val}
                            key={val}
                            onTabSelect={selectSubject}
                            selected={isSelected}
                        >
                            <Text weight={isSelected ? Text.WEIGHT.M : Text.WEIGHT.S}>{subject.name}</Text>
                        </Tabs.Tab>
                    );
                })}
            </Tabs>
        </>
    );
}

function getIsPropsEqual<S extends CourseSubject = CourseSubject, V = string>(
    prevProps: Props<S, V>,
    nextProps: Props<S, V>
) {
    return (
        isEqual(prevProps.metrikaParams, nextProps.metrikaParams) &&
        isEqual(prevProps.subjects, nextProps.subjects) &&
        prevProps.currentSubject === nextProps.currentSubject &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.mix === nextProps.mix &&
        prevProps.onSelect === nextProps.onSelect
    );
}

const memoized = React.memo(SubjectsNavList, getIsPropsEqual);
memoized.displayName = 'SubjectsNavList';

export default memoized;
